import React from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import {
  useScreenTitle,
  useScreenDispatch,
  useScreenState,
  ScreenLink,
} from '@gogame/screens';

import {
  AudienceFormEdit,
  AudienceFormDuplicate,
} from 'scenes/CustomAudiences/Audiences/components/AudienceForm';
import { FileBasedAudienceFormEdit } from 'scenes/CustomAudiences/Audiences/components/FileBasedAudienceForm';
import PartnerAudienceDetails from 'scenes/CustomAudiences/PartnerAudiences/components/PartnerAudienceDetails';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_FILTERSTATUS,
  COLUMN_SIZE,
  COLUMN_ATT_EXCLUDED_SIZE,
  COLUMN_PARTNER_AUDIENCES,
  COLUMN_TYPE,
  COLUMN_SYNC_DATE,
  COLUMN_NETWORKS,
  COLUMN_PLATFORMS,
  COLUMN_COUNTRIES,
  COLUMN_APPLICATIONS,
  COLUMN_UPDATED_DATE,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
  COLUMN_OWNER,
  TYPE_AUDIENCE,
  TYPE_FILE_BASED_AUDIENCE,
} from 'scenes/CustomAudiences/Audiences/constants';
import {
  useIsUpdating,
  useLoadAudienceTable,
} from 'scenes/CustomAudiences/Audiences/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/CustomAudiences/Audiences/screenReducer';
import {
  PartnerAudienceFormCreate,
  PARENT_AUDIENCE,
} from 'scenes/CustomAudiences/PartnerAudiences/components/PartnerAudienceForm';
import { audiencesDetailsLink } from 'scenes/CustomAudiences/Audiences/navigation';
import ActionsComponent from 'scenes/CustomAudiences/Audiences/components/Actions';
import { getColor } from 'scenes/CustomAudiences/Audiences/components/Status';

import Toolbar from './Toolbar';
import BatchToolbar from './BatchToolbar';
import ApplicationsColumn from './ApplicationsColumn';
import { Link } from '@gogame/components';

const AudiencesDataTable = () => {
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const [screenTitle] = useScreenTitle();
  const [childComponent, openChildComponent] = useChildComponent();
  const { filters, tableSettings, searchQuery } = screenState;
  const { data, isLoading, isRefetching } = useLoadAudienceTable({
    filters,
    search: searchQuery,
    ...tableSettings,
  });

  const isUpdating = useIsUpdating();
  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleEdit = ({ id, type }) => {
    let formEditComponent;

    switch (type?.id) {
      case TYPE_AUDIENCE:
        formEditComponent = AudienceFormEdit;
        break;
      case TYPE_FILE_BASED_AUDIENCE:
        formEditComponent = FileBasedAudienceFormEdit;
        break;
      default:
        break;
    }

    openChildComponent(formEditComponent, {
      id,
    });
  };

  const handleDuplicate = async ({ id }) => {
    openChildComponent(AudienceFormDuplicate, {
      id,
    });
  };

  const handleAddPartnerAudiences = async ({ id }) => {
    openChildComponent(PartnerAudienceFormCreate, {
      initialValues: { [PARENT_AUDIENCE]: id },
    });
  };

  const handleOpenBusinessPartner = async (_, { id }) => {
    openChildComponent(PartnerAudienceDetails, { id });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading || isUpdating}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        storageKey={'audiences'}
        defaultColumnWidth={160}
        checkboxSelection={true}
        toolbar={<Toolbar />}
        selectedRowsToolbarActions={({ selectedValues }) => (
          <BatchToolbar
            selectedValues={selectedValues}
            openChildComponent={openChildComponent}
          />
        )}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'text'}
          headerName={'Name'}
          width={322}
          fixed={true}
          resizable={false}
          formatter={(value, { id }) => (
            <ScreenLink link={audiencesDetailsLink} pathParams={{ id }}>
              {value}
            </ScreenLink>
          )}
        />
        <DataGrid.Column
          headerName={'Status'}
          field={COLUMN_FILTERSTATUS}
          width={110}
          type={'status'}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_SIZE}
          type={'number'}
          headerName={'Size'}
          width={104}
        />
        <DataGrid.Column
          field={COLUMN_ATT_EXCLUDED_SIZE}
          type={'number'}
          headerName={'ATT Excluded Size'}
          width={111}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_SYNC_DATE}
          headerName={'Last List Update'}
          width={140}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_TYPE}
          type={'text'}
          headerName={'Type'}
          width={100}
          sortable={false}
          getValue={(value) => value?.name}
        />
        <DataGrid.Column
          field={COLUMN_PARTNER_AUDIENCES}
          headerName={'Partner Audiences'}
          type={'list'}
          minWidth={210}
          limit={1}
          sortable={false}
          getItemValue={(v) => {
            return (
              <Link onClick={(e) => handleOpenBusinessPartner(e, v)}>
                {v.name}
              </Link>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_NETWORKS}
          headerName={'Networks'}
          type={'list'}
          limit={1}
        />
        <DataGrid.Column
          field={COLUMN_PLATFORMS}
          headerName={'Platforms'}
          type={'list'}
          limit={1}
        />
        <DataGrid.Column
          field={COLUMN_COUNTRIES}
          headerName={'Countries'}
          type={'list'}
          limit={1}
          placeholder={'All'}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATIONS}
          headerName={'Applications'}
          renderCell={(props) => <ApplicationsColumn {...props} />}
        />
        <DataGrid.Column
          field={COLUMN_OWNER}
          type={'text'}
          headerName={'Owner'}
          width={166}
          sortable={false}
          getValue={(value) => value?.name}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          headerName={'Last Updated'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          type={'text'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onAddPartnerAudiences={handleAddPartnerAudiences}
          onOpenChildComponent={openChildComponent}
          actionsComponent={ActionsComponent}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default AudiencesDataTable;
